import { useEffect, useId, useState, type ReactNode } from 'react';
import { ArrowLeft, CalendarClock, Clock } from 'lucide-react';
import { errorMessage, useT } from '../../../i18n';
import {
  bookAtVenue,
  readVenue,
  readVenueAvailability,
  type Venue,
  type VenueBookingTerms,
} from '../../../lib/api/venues';
import type { MyClub } from '../../../lib/api/myClubs';
import { BigButton, Card, EmptyState, SectionTitle } from '../mobileUi';
import { VenueAvailability, type VenueSlotPick } from './VenueAvailability';
import { VenueBooking } from './VenueBooking';

// ─────────────────────────────────────────────────────────────────────
// ONE VENUE, FROM A «FINN EN KLUBB» RESULT (#833).
//
// The directory used to end in a name and a button. This is where a result
// leads: what the place is — its courts, the opening hours its calendar
// enforces — and a clear way on.
//
// ── THE WAY ON IS WHAT THE SERVER SAYS, AND NO MORE ──────────────
//
// EVERYBODY SEES WHAT IS FREE (#833 slice 2): `VenueAvailability`, the
// member booking's own free times. Membership is a price, never access
// (owner, 2026-10-16), so a member and a non-member see the same times — each
// at THEIR price, which the server sends with the times (slice 3a) and this
// page only shows.
//
// A MEMBER books through the member booking they already have: «Book bane»
// selects the club and opens Spill › Book. Nothing here duplicates it.
//
// ANYBODY ELSE (#833 slice 3b) is told what the venue's door would do for them
// — the read's `booking: { offered, reason }`, never a guess:
//
//   offered                    pick a free time, then `VenueBooking` books it
//                              through `POST /venues/:slug/bookings` and pays
//                              with the member app's own «Betal nå».
//   guest_booking_not_offered  «Klubben tar ikke imot bookinger fra gjester»,
//                              the free times without prices, and no «Book»
//                              (owner decision 2026-11-04, on #1368).
//   no_player                  a player profile is needed first: the way to
//                              make one is Profil's «Opprett spillerprofil».
//   court_prices_not_set       the venue has set no prices, so nobody books.
//
// The application stays exactly what it was: «Søk om medlemskap», with the
// same outcomes, owned by `MobileFindClub`.
//
// Nothing here claims what membership gives beyond booking. That is not
// decided, and the owner said not to guess it.
// ─────────────────────────────────────────────────────────────────────

export interface MobileVenueProps {
  slug: string;
  /** The search result's name, so the heading does not wait for the read. */
  fallbackName: string;
  /** The search result's kind, for the same reason. */
  kind: string;
  /** The player's membership here, or null. Decides the way on. */
  membership: MyClub | null;
  onBack: () => void;
  onSelect: (club: MyClub) => void;
  /** Select the club and open its booking. Absent: a member is told, and offered no button. */
  onBook?: (club: MyClub) => void;
  onApply: () => void;
  applying: boolean;
  applyDisabled: boolean;
  /**
   * The club's answer means this person may not ask now (#1302): the button is
   * held shut, and `outcome` says why — with the date, when there is one.
   */
  closed?: boolean;
  /** An application to this club stands: say so instead of offering it again. */
  pending?: boolean;
  /**
   * Take the standing application back (#729). Offered under «Venter på
   * godkjenning» when given; the person may apply again at once.
   */
  onWithdraw?: () => void;
  withdrawing?: boolean;
  /**
   * What the club answered to this player's application, if they asked — a
   * node, because a throttled one is a live countdown (#778 Q7).
   */
  outcome: ReactNode | null;
  /** The profile step, in place of the application, while it is open. */
  profileStep?: ReactNode;
  /** Injected in tests; the real one is `GET /venues/:slug`. */
  load?: typeof readVenue;
  /** Injected in tests; the real one is `GET /venues/:slug/availability`. */
  loadAvailability?: typeof readVenueAvailability;
  /**
   * Where an account without a player profile makes one (#833 slice 3b):
   * Profil's «Opprett spillerprofil». Absent: the page says a profile is
   * needed, and offers no button.
   */
  onCreateProfile?: () => void;
  /** Injected in tests; the real one is `POST /venues/:slug/bookings`. */
  bookAtVenue?: typeof bookAtVenue;
}

type State =
  | { state: 'loading' }
  | { state: 'ok'; venue: Venue }
  | { state: 'not_found' }
  | { state: 'failed' };

export function MobileVenue({
  slug,
  fallbackName,
  kind,
  membership,
  onBack,
  onSelect,
  onBook,
  onApply,
  applying,
  applyDisabled,
  closed = false,
  pending = false,
  onWithdraw,
  withdrawing = false,
  outcome,
  profileStep,
  load = readVenue,
  loadAvailability = readVenueAvailability,
  onCreateProfile,
  bookAtVenue: book = bookAtVenue,
}: MobileVenueProps) {
  const t = useT();
  const outcomeId = `venue-outcome-${useId().replace(/:/g, '')}`;
  const [result, setResult] = useState<State>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  /** The caller's terms, as the availability read last said them. Null: not known. */
  const [terms, setTerms] = useState<VenueBookingTerms | null>(null);
  /** The free time picked to book, or null. */
  const [pick, setPick] = useState<VenueSlotPick | null>(null);
  /** Bumped to read the free times again — after a booking, or a refusal that says they moved. */
  const [refresh, setRefresh] = useState(0);
  /** Why the last booking was refused, said over the fresh list it caused. */
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setResult({ state: 'loading' });
    void load(slug, controller.signal).then((outcome) => {
      if (outcome.status === 'aborted') return;
      if (outcome.status === 'ok') setResult({ state: 'ok', venue: outcome.venue });
      else if (outcome.status === 'not_found') setResult({ state: 'not_found' });
      else setResult({ state: 'failed' });
    });
    return () => controller.abort();
  }, [slug, load, attempt]);

  const venue = result.state === 'ok' ? result.venue : null;
  const name = venue?.name ?? fallbackName;
  const kindOf = venue?.kind ?? kind;
  const kindLabel =
    kindOf === 'CENTRE'
      ? t('clubDiscovery.kindCentre')
      : kindOf === 'CLUB'
        ? t('clubDiscovery.kindClub')
        : null;

  return (
    <div className="pb-4">
      <div className="flex items-center gap-2 px-4 pt-2">
        <button
          onClick={onBack}
          aria-label={t('mobile.common.back')}
          className="p-1 -ml-1 text-ink-3"
        >
          <ArrowLeft size={18} />
        </button>
        <h1
          className="font-display font-semibold text-ink leading-tight truncate flex-1"
          style={{ fontSize: 20 }}
        >
          {name}
        </h1>
        {kindLabel && (
          <span
            className="font-mono text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0"
            style={{ background: '#eef0ea', color: '#4a544c' }}
          >
            {kindLabel}
          </span>
        )}
      </div>

      <div className="px-4 mt-3 space-y-4 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-x-8 @wide:gap-y-4 @wide:items-start">
        {result.state === 'loading' && <EmptyState title={t('mobile.venue.loading')} />}
        {result.state === 'not_found' && <EmptyState title={t('mobile.venue.notFound')} />}
        {result.state === 'failed' && (
          <div className="space-y-2">
            <EmptyState title={t('mobile.venue.failed')} />
            <BigButton variant="ghost" onClick={() => setAttempt((n) => n + 1)}>
              {t('mobile.venue.retry')}
            </BigButton>
          </div>
        )}

        {venue && (
          <>
            <div>
              <SectionTitle>{t('mobile.centre.openingHours')}</SectionTitle>
              <Card>
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ width: 34, height: 34, background: '#0f1611', color: '#fff' }}
                  >
                    <Clock size={16} />
                  </div>
                  <div className="min-w-0 flex-1 text-[14px] font-semibold text-ink">
                    {t('mobile.venue.everyDay')}
                  </div>
                  {venue.openingHours ? (
                    <div className="font-mono font-bold text-[14px] text-lime-deep tabular-nums">
                      {venue.openingHours.opens}–{venue.openingHours.closes}
                    </div>
                  ) : (
                    <div className="text-[12px] text-ink-3">{t('mobile.venue.noHours')}</div>
                  )}
                </div>
              </Card>
            </div>

            <div>
              <SectionTitle
                action={
                  venue.courts.length > 0 ? (
                    <span className="text-[12px] text-ink-3">
                      {venue.courts.length === 1
                        ? t('mobile.venue.courtsOne')
                        : t('mobile.venue.courtsMany', { count: venue.courts.length })}
                    </span>
                  ) : undefined
                }
              >
                {t('mobile.centre.courts')}
              </SectionTitle>
              {venue.courts.length === 0 ? (
                <EmptyState title={t('mobile.centre.noCourts')} />
              ) : (
                <div className="space-y-2">
                  {venue.courts.map((court) => (
                    <Card key={court.number}>
                      <div className="flex items-center gap-3">
                        <div
                          className="flex items-center justify-center rounded-xl flex-shrink-0 font-display font-semibold"
                          style={{ width: 40, height: 40, background: '#eef0ea', fontSize: 15 }}
                        >
                          {court.number}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-ink truncate">
                            {court.name ?? t('mobile.venue.courtNumber', { number: court.number })}
                          </div>
                          <div className="text-[11px] text-ink-3">
                            {court.type === 'single'
                              ? t('mobile.venue.courtSingle')
                              : t('mobile.venue.courtDouble')}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* WHAT IS FREE — only where the centre has a calendar: stored hours
                and courts, the same «configured» the sections above use. */}
            {venue.openingHours && venue.courts.length > 0 && (
              <VenueAvailability
                slug={slug}
                courts={venue.courts}
                load={loadAvailability}
                onTerms={setTerms}
                // A MEMBER books through the member booking; only somebody
                // else picks a time here — and only where the server offers it.
                pickable={!membership}
                onPick={(p) => {
                  setPick(p);
                  if (p) setNotice(null);
                }}
                refresh={refresh}
                picked={pick?.slot.startTime ?? null}
              />
            )}

            {/* THE WAY ON. A member books through the member booking; anybody
                else is told what the venue's door would do for them, and may
                apply. */}
            <div className="space-y-4">
              {venue.courts.length > 0 && (membership || terms) && (
                <div>
                  <SectionTitle>{t('mobile.venue.booking')}</SectionTitle>
                  {membership ? (
                    <Card>
                      <p className="text-[13px] text-ink-2">{t('mobile.venue.memberHere')}</p>
                      {onBook && (
                        <div className="mt-3">
                          <BigButton variant="lime" onClick={() => onBook(membership)}>
                            {t('mobile.venue.book')}
                          </BigButton>
                        </div>
                      )}
                    </Card>
                  ) : terms && terms.offered ? (
                    <div className="space-y-2">
                      {notice && (
                        <p role="alert" className="text-[13px] text-rose-700 px-1">
                          {notice}
                        </p>
                      )}
                      {pick ? (
                        <VenueBooking
                          // A new pick is a new booking: nothing carried over.
                          key={`${pick.date}:${pick.durationMin}:${pick.slot.startTime}`}
                          slug={slug}
                          pick={pick}
                          courts={venue.courts}
                          book={book}
                          onStale={(message) => {
                            // The pick was made from a list that has moved: it
                            // goes, the sentence stays, and the day is read again.
                            setPick(null);
                            setNotice(message);
                            setRefresh((n) => n + 1);
                          }}
                          // What was just booked is no longer free. The pick —
                          // and with it the confirmation — stays.
                          onBooked={() => setRefresh((n) => n + 1)}
                          onDone={() => setPick(null)}
                        />
                      ) : (
                        <EmptyState
                          icon={<CalendarClock size={22} />}
                          title={t('mobile.venue.pickTimeTitle')}
                          hint={t('mobile.venue.pickTimeHint')}
                        />
                      )}
                    </div>
                  ) : terms && terms.reason === 'no_player' ? (
                    <Card>
                      <p className="text-[13px] text-ink-2" data-testid="venue-not-bookable">
                        {t('mobile.venue.needsProfile')}
                      </p>
                      {onCreateProfile && (
                        <div className="mt-3">
                          <BigButton variant="lime" onClick={onCreateProfile}>
                            {t('mobile.identity.create')}
                          </BigButton>
                        </div>
                      )}
                    </Card>
                  ) : terms ? (
                    <Card>
                      <p className="text-[13px] text-ink-2" data-testid="venue-not-bookable">
                        {/* The door's own code, worded by the catalog. No reason
                            at all is read as the default: guests are not taken. */}
                        {errorMessage(t, terms.reason ?? 'guest_booking_not_offered')}
                      </p>
                    </Card>
                  ) : null}
                </div>
              )}

              {membership ? (
                <BigButton variant="ghost" onClick={() => onSelect(membership)}>
                  {t('mobile.venue.select')}
                </BigButton>
              ) : (
                <div>
                  <SectionTitle>{t('mobile.venue.membership')}</SectionTitle>
                  {profileStep ?? (
                    <>
                      {pending ? (
                        <>
                          <BigButton variant="ghost" disabled>
                            {t('clubDiscovery.pending')}
                          </BigButton>
                          {/* TAKING IT BACK (#729): no confirmation step,
                              because nothing is lost — they may apply again
                              straight away. */}
                          {onWithdraw && (
                            <button
                              type="button"
                              className="mt-2 w-full text-center text-[13px] font-semibold text-ink-2 underline disabled:opacity-40"
                              onClick={onWithdraw}
                              disabled={withdrawing}
                            >
                              {withdrawing
                                ? t('clubDiscovery.withdrawing')
                                : t('clubDiscovery.withdraw')}
                            </button>
                          )}
                        </>
                      ) : (
                        <BigButton
                          onClick={onApply}
                          disabled={applyDisabled || closed}
                          aria-describedby={closed && outcome ? outcomeId : undefined}
                        >
                          {applying ? t('clubDiscovery.asking') : t('clubDiscovery.ask')}
                        </BigButton>
                      )}
                      {outcome && (
                        <p
                          id={outcomeId}
                          role="status"
                          className="text-[12px] text-ink-3 mt-2 px-1"
                        >
                          {outcome}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
